/* eslint-disable */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

// local component imports
import CustomImage from "../../../../../components/atoms/customImage";

// services and utils imports
import { requestMobileStoreFeed } from "../../../../../store/slices/dynamicHomeSlice";
import CookieUtil from "../../../../../utils/storage/cookieUtil";
import {
  getStoreDisplayName,
  getStorePageUrl,
  replaceBrandStoreUrl,
} from "../../../../../utils/dynamicHomeUtils/storeUrl";

/**
 * if a function doesn't use hooks and doesn't close over component state/props, it belongs outside — it's a performance and clarity best practice in React.
 */
const getBackgroundStyle = (bg) => {
  switch (bg?.mediaType) {
    case "gradient":
      return { background: bg.mediaValue };
    case "color":
      return { backgroundColor: bg.mediaValue };
    default:
      return {};
  }
};

/**
 */
const getBorderStyle = (border) => {
  if (!border) return {};
  const { radiusPx, thicknessPx, border: borderMedia } = border;
  const borderColor = borderMedia?.mediaValue;
  return {
    ...(radiusPx !== undefined && { borderRadius: `${radiusPx}px` }),
    ...(thicknessPx && borderColor && { border: `${thicknessPx}px solid ${borderColor}` }),
  };
};

/**
 * Smoothly scrolls a container to keep the target tab in view using a cubic ease-in-out animation.
 */
const smoothScrollTabIntoView = (container, tab) => {
  const PADDING = 40;
  const DURATION = 420;

  const tabLeft = tab.offsetLeft;
  const tabRight = tabLeft + tab.offsetWidth;
  const containerScrollLeft = container.scrollLeft;
  const containerWidth = container.offsetWidth;

  let targetScrollLeft = null;
  if (tabLeft - PADDING < containerScrollLeft) {
    targetScrollLeft = tabLeft - PADDING;
  } else if (tabRight + PADDING > containerScrollLeft + containerWidth) {
    targetScrollLeft = tabRight + PADDING - containerWidth;
  }

  if (targetScrollLeft === null) return () => {};

  const startScrollLeft = containerScrollLeft;
  const delta = targetScrollLeft - startScrollLeft;
  let startTime = null;
  let rafId;

  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  const step = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / DURATION, 1);
    container.scrollLeft = startScrollLeft + delta * easeInOutCubic(progress);
    if (progress < 1) {
      rafId = requestAnimationFrame(step);
    }
  };

  rafId = requestAnimationFrame(step);
  return () => cancelAnimationFrame(rafId);
};

/**
 * Idle-loads inactive brand tab images so they are warm in the browser cache
 * before the user taps a different store tab.
 * Active images are already preloaded server-side by MastHeadPreload.
 *
 * Uses requestIdleCallback so the browser only fetches when the main thread
 * has nothing better to do (scrolling, animations, etc.). The 10s timeout
 * is a hard fallback in case the browser is never idle within that window.
 *
 * Google crawler: Googlebot's JS execution budget is ~5s — the callback
 * never fires for crawlers, so no SEO or crawl-budget impact.
 * LCP window: Google measures LCP at ~2.5s — 10s is safely past that.
 *
 * @param {Array} mastHeadData - Array of brand store objects.
 */
const preloadBrandImages = (mastHeadData) => {
  if (typeof window === "undefined") return;
  mastHeadData?.forEach((brand) => {
    const inactiveUrl = brand?.storeStyle?.img?.inActiveUrl;
    if (!inactiveUrl) return;

    const load = () => {
      const img = new window.Image();
      img.src = inactiveUrl;
    };

    // requestIdleCallback — fires only when browser main thread is free.
    // timeout: 10000 — hard fallback if browser never goes idle within 10s.
    if ("requestIdleCallback" in window) {
      requestIdleCallback(load, { timeout: 10000 });
    } else {
      // Safari fallback — no requestIdleCallback support
      setTimeout(load, 10000);
    }
  });
};

/**
 * Mobile masthead brand tabs. Soft-updates `/` or `/store/[slug]` and loads that brand's feed.
 * @param {Object} props
 * @param {Array<object>} props.mastHeadData - Masthead stores from Redux / SSR
 * @returns {JSX.Element}
 */
const BrandTabs = ({ mastHeadData }) => {
  const dispatch = useDispatch();
  const activeBrand = mastHeadData?.find((s) => s.isSelected) ?? mastHeadData?.[0];
  const inActiveBg = activeBrand?.storeStyle?.background?.inActiveForOthers;
  const tabRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  const email = CookieUtil.getCookie("faul") || "";
  const fnpId = CookieUtil.getCookie("ab_user_id");
  const pincode = useSelector((state) => state?.locationLock?.selectedDomesticLocation?.pincode);

  const selectedIdx = mastHeadData?.findIndex((s) => s.isSelected) ?? 0;
  // ≤4 stores fill the row; 5+ keep a fixed 4-up size with a peek of the next tab to hint scroll.
  const shouldScroll = (mastHeadData?.length ?? 0) > 4;
  // Reserve 1rem for the fourth gutter and a subtle peek of the fifth tab.
  const tabClassName = shouldScroll
    ? "shrink-0 basis-[calc((100%-2.25rem-1rem)/4)] overflow-hidden shadow-[0_1px_2px_0_rgba(0,0,0,0.1)] duration-300"
    : "min-w-0 flex-1 overflow-hidden shadow-[0_1px_2px_0_rgba(0,0,0,0.1)] duration-300";

  useEffect(() => {
    preloadBrandImages(mastHeadData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tab = tabRefs.current[selectedIdx];
    const container = scrollContainerRef.current;
    if (!tab || !container) return;
    return smoothScrollTabIntoView(container, tab);
  }, [selectedIdx]);

  return (
    <nav aria-label="Brand stores">
      <div
        ref={scrollContainerRef}
        className="scrollbar-hide -mx-1 flex gap-12 overflow-x-auto px-[15px] py-[10px] xxs:py-[12px] xxsplus:py-[14px] xxsmax:py-[16px]"
      >
        {mastHeadData?.map((brand, index) => {
          const isActive = brand.isSelected ?? index === 0;
          const backgroundStyle = getBackgroundStyle(isActive ? brand.storeStyle?.background?.active : inActiveBg);
          const borderStyle = getBorderStyle(brand.storeStyle?.border);

          const imageUrl = brand.storeStyle?.img?.url;
          const imageUrlInactive = brand.storeStyle?.img?.inActiveUrl;
          const imageAlt = brand.storeStyle?.img?.alt || brand.name || "Brand";
          const brandLabel = getStoreDisplayName(brand) || brand.name || `Brand ${index + 1}`;

          /**
           * Soft URL replace to `/` or `/store/[slug]`, then request feed by `storeName`.
           * `storeId` is only for Redux slot matching — not sent on the masthead API.
           */
          const onTabClick = () => {
            if (isActive) {
              return;
            }

            // history.replaceState avoids Next.js router navigation / SSR re-fetch.
            replaceBrandStoreUrl(getStorePageUrl(brand, mastHeadData));

            dispatch(
              requestMobileStoreFeed({
                storeId: brand.storeId,
                storeName: getStoreDisplayName(brand),
                stores: mastHeadData,
                pincode,
                email,
                fnpId,
                forceCall: true,
              }),
            );
          };

          return (
            <button
              key={brand.storeId ?? brand.id ?? index}
              ref={(el) => (tabRefs.current[index] = el)}
              aria-label={brandLabel}
              aria-pressed={isActive}
              className={tabClassName}
              style={{
                aspectRatio: "75 / 36",
                ...backgroundStyle,
                ...borderStyle,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                transition: "background 0.3s ease, border 0.3s ease",
                border: "1px solid rgba(0, 0, 0, 0.16)",
              }}
              onClick={onTabClick}
            >
              {imageUrl && (
                <div
                  key={isActive ? imageUrl : imageUrlInactive?.length > 0 ? imageUrlInactive : imageUrl}
                  className="h-full w-full"
                >
                  <CustomImage
                    src={isActive ? imageUrl : imageUrlInactive?.length > 0 ? imageUrlInactive : imageUrl}
                    alt={imageAlt}
                    className="h-full w-full object-contain"
                    width={100}
                    height={100}
                    placeholder="empty"
                    isPriority={true}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

BrandTabs.propTypes = {
  mastHeadData: PropTypes.arrayOf(PropTypes.object),
};

/**
 * Only re-render BrandTabs when tab identity or selection changes.
 * Ignores feed/curatedWidgets updates so saga resolution doesn't cause a scroll reset.
 */
const areTabsEqual = (prev, next) => {
  const p = prev.mastHeadData;
  const n = next.mastHeadData;
  if (!p || !n || p.length !== n.length) return false;
  return p.every((store, i) => store.storeId === n[i].storeId && store.isSelected === n[i].isSelected);
};

export default React.memo(BrandTabs, areTabsEqual);
